import * as FA from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Hls from "hls.js";
import classNames from "classnames";
import React, { Component } from "react";
import { PlayerProps } from "./properties";
import styles from "./Media.module.css";
import {
    isNullOrUndefined,
    isNullOrUndefinedOrZero,
} from "../src/Utils/Varios";

export interface MediaProps {
    // ACL token
    aclToken: string;
    // Auto play after loading
    autoplay?: boolean;
    // Set a pause at given date
    pauseAt?: Date;
    // Global player properties
    playerProps: PlayerProps;
    // Share token
    shareToken?: string;
    // When player should start
    start: Date;
    // Force streamer url
    streamerURL?: string;
    // Expose media element to parent
    exposeMedia?: (player: HTMLMediaElement) => void;
    // On player play / pause
    onPlayPause?(playing: boolean): void;
    // On player timeupdate
    onTimeUpdate?(time: Date): void;
    // On time selection
    onSelectTimeChange?(start?: Date, end?: Date): void;
}

interface MediaState {
    mediaID: number | null;
    // Current timestamp.
    now: Date;
    // Current timestamp end.
    endNow: Date;
    // Player is currently playing
    playing: boolean;
    // Is player ready ?
    ready: boolean;
    // Play speed
    speed: 1 | 2 | 5 | 0.5;
    // Volume
    volume: 0 | 0.25 | 0.5 | 0.75 | 1;
    startTime?: Date;
    endTime?: Date;
}

export interface MediaSource {
    id: number;
    mdiaSource: string;
    sinceTimestamp: number;
    type: number;
}

const dateOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
};

export class Media extends Component<MediaProps, MediaState> {
    //@ts-ignore
    private firstFragTime: Date | undefined;
    private hls: Hls | null = null;
    private interval: number | null = null;
    private pauseAt: Date | undefined = undefined;
    private player: HTMLVideoElement | null = null;
    private start: Date;
    private hlsBindings: Map<string, any>;
    private streamerURL =
        this.props.streamerURL || "https://streamer.yacast.fr/v0/public";

    constructor(props: MediaProps) {
        super(props);
        this.start = props.start;
        this.hlsBindings = this.getHLSBindings();
        this.state = {
            mediaID: null,
            now: new Date(props.start),
            endNow: new Date(props.start),
            playing: false,
            ready: false,
            speed: 1,
            volume: 0.75,
        };
    }

    public componentWillUnmount() {
        this.destroyAll();
    }

    private destroyAll = () => {
        if (this.hls !== null) {
            this.hls.detachMedia();
            this.hls.destroy();
            this.hls = null;
        }
        if (this.interval !== null) {
            window.clearInterval(this.interval);
            this.interval = null;
        }
    };

    private restartAll = () => {
        if (!Hls.isSupported() || this.player === null) {
            return;
        }
        this.pauseAt = this.props.pauseAt;

        this.hls = new Hls({
            xhrSetup: (xhr: any) => {
                if (this.player === null) {
                    return;
                }
                if (!this.props.shareToken) {
                    xhr.setRequestHeader(
                        "authorization",
                        `Bearer ${this.props.aclToken}`
                    );
                }
                xhr.setRequestHeader(
                    "x-yacast-streamer-client",
                    "secure-player"
                );
            },
        });
        const ts: number = Math.round(this.props.start.getTime() / 1000);
        const end: number = Math.round(
            this.props.start.getTime() / 1000 +
                this.props.playerProps.timelineDuration
        );

        this.hls.attachMedia(this.player);
        this.hlsBindings.forEach((callback: any, event: string) => {
            // @ts-ignore
            this.hls.on(event as any, callback);
        });
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
            this.interval = window.setInterval(() => {
                if (this.player === null) {
                    return;
                }
                const now =
                    this.start.getTime() + this.player.currentTime * 1000;
                this.setState({ now: new Date(now) });
            }, 250);
            if (this.player === null) {
                return;
            }
            this.setState({
                playing: false,
                ready: true,
            });
            this.player.playbackRate = this.state.speed;
            this.player.volume = this.state.volume;
            if (this.props.autoplay === true) {
                this.player
                    .play()
                    .then(() => {
                        this.setState({ playing: true });
                        if (typeof this.props.onPlayPause !== "undefined") {
                            this.props.onPlayPause(true);
                        }
                    })
                    .catch((err) => {
                        console.warn(err);
                        this.setState({
                            playing: false,
                            ready: false,
                        });
                        if (typeof this.props.onPlayPause !== "undefined") {
                            this.props.onPlayPause(false);
                        }
                    });
            }
        });
        this.player.ontimeupdate = () => {
            if (this.player === null) {
                return;
            }

            const now = new Date(
                this.props.start.getTime() + this.player.currentTime * 1000
            );

            if (typeof this.props.onTimeUpdate !== "undefined") {
                this.props.onTimeUpdate(now);
            }
            if (
                typeof this.pauseAt !== "undefined" &&
                now.getTime() >= this.pauseAt.getTime()
            ) {
                this.player.pause();
                this.pauseAt = undefined;
            }
        };
        if (this.props.shareToken) {
            this.hls.loadSource(
                `${this.streamerURL}/share/${this.props.shareToken}`
            );
            return;
        }
        fetch(
            `https://hyperion-public-api.yacast.fr/rest/v2/medias/available/${this.props.playerProps.mediaID}`,
            { method: "GET", mode: "cors", cache: "default" }
        )
            .then((res: Response) => {
                return res.json();
            })
            .then((json: any) => {
                if (this.hls === null) {
                    return;
                }
                if (json.sinceTimestamp === 0 || ts <= json.sinceTimestamp) {
                    let typeStr = "radio";

                    switch (json.type) {
                        case 2:
                            typeStr = "tv";
                            break;
                        case 1:
                        default:
                            typeStr = "radio";
                            break;
                    }
                    this.hls.loadSource(
                        `https://ma-stream-dev.yacast.fr/${typeStr}/${json.mediaSource}/${ts}/master.m3u8`
                    );
                    return;
                }
                this.hls.loadSource(
                    `${this.streamerURL}/instant?media=${json.mediaSource}&start=${ts}&end=${end}`
                );
                /*
                this.hls.loadSource(
                    `https://pcache3.yacast.fr/pl/get/${json.mediaSource}/${ts}/${end}`
                );
                */
            })
            .catch((err: Error) => {
                console.log(err);
            });
    };

    public componentDidMount() {
        this.setState((_prevState, props) => ({
            mediaID: props.playerProps.mediaID,
        }));
        this.restartAll();
    }

    startTimeHandler = () => {
        this.setState(
            (prevState) => ({ startTime: prevState.now }),
            () => {
                if (this.props.onSelectTimeChange) {
                    this.props.onSelectTimeChange(
                        this.state.startTime,
                        this.state.endTime
                    );
                }
            }
        );
    };

    endTimeHandler = () => {
        this.setState(
            (prevState) => ({ endTime: prevState.now }),
            () => {
                if (this.props.onSelectTimeChange) {
                    this.props.onSelectTimeChange(
                        this.state.startTime,
                        this.state.endTime
                    );
                }
                this.onPlayPauseClick();
            }
        );
    };

    public componentDidUpdate() {
        const props_start = this.props.start;
        const this_start = this.start;
        if (this_start.valueOf() !== props_start.valueOf()) {
            this.destroyAll();
            this.start = props_start;
            if (this.props.playerProps.mediaID !== this.state.mediaID) {
                this.setState(
                    (_prevState, props) => ({
                        mediaID: props.playerProps.mediaID,
                    }),
                    this.restartAll
                );
            } else {
                this.restartAll();
            }
        }
    }

    public render() {
        let speedText = "1x";
        switch (this.state.speed) {
            case 5:
                speedText = "5x";
                break;
            case 2:
                speedText = "2x";
                break;
            case 1:
                speedText = "1x";
                break;
            case 0.5:
            default:
                speedText = "½x";
                break;
        }
        let volumeIcon = FA.faVolume;
        switch (this.state.volume) {
            case 0:
                volumeIcon = FA.faVolumeMute;
                break;
            case 0.25:
                volumeIcon = FA.faVolumeOff;
                break;
            case 0.5:
                volumeIcon = FA.faVolumeDown;
                break;
            case 1:
                volumeIcon = FA.faVolumeUp;
                break;
            case 0.75:
            default:
                volumeIcon = FA.faVolume;
                break;
        }
        const hours = this.state.now.getHours();
        const minutes = this.state.now.getMinutes();
        const seconds = this.state.now.getSeconds();
        const exposeMedia = this.props.exposeMedia;
        // @ts-ignore
        return (
            <div>
                <div className={styles.container}>
                    <video
                        onClick={() => this.onPlayPauseClick()}
                        className={styles.videoElm}
                        ref={(player) => {
                            if (
                                player !== this.player &&
                                !isNullOrUndefined(exposeMedia)
                            ) {
                                exposeMedia(player as HTMLMediaElement);
                            }
                            this.player = player;
                            return player;
                        }}
                    />
                    <div className={styles.controlBar}>
                        <div
                            className={classNames(
                                styles.btn,
                                styles.playPauseBtn
                            )}
                            onClick={this.onPlayPauseClick.bind(this)}
                        >
                            {this.state.ready ? (
                                this.state.playing ? (
                                    <FontAwesomeIcon icon={FA.faPause} />
                                ) : (
                                    <FontAwesomeIcon icon={FA.faPlay} />
                                )
                            ) : (
                                <FontAwesomeIcon icon={FA.faSpinner} />
                            )}
                        </div>
                        <div
                            className={classNames(styles.btn, styles.speedBtn)}
                            onClick={this.onSpeedClick.bind(this)}
                        >
                            <span>{speedText}</span>
                        </div>
                        <div
                            className={classNames(styles.btn, styles.rightBtn)}
                        >
                            <button
                                onClick={this.startTimeHandler.bind(this)}
                                disabled={false}
                            >
                                <FontAwesomeIcon
                                    color="white"
                                    icon={FA.faArrowCircleRight}
                                />
                            </button>
                        </div>
                        <div className={classNames(styles.btn, styles.leftBtn)}>
                            <button
                                onClick={this.endTimeHandler.bind(this)}
                                disabled={false}
                            >
                                <FontAwesomeIcon
                                    color="white"
                                    icon={FA.faArrowCircleLeft}
                                />
                            </button>
                        </div>
                        <div
                            className={classNames(styles.btn, styles.volumeBtn)}
                            onClick={this.onVolumeClick.bind(this)}
                        >
                            <FontAwesomeIcon icon={volumeIcon} />
                        </div>
                        <div
                            className={classNames(styles.btn, styles.fullBtn)}
                            onClick={this.onFSClick.bind(this)}
                        >
                            <FontAwesomeIcon icon={FA.faCompress} />
                        </div>
                        <div className={styles.playerInfos}>
                            <span className={styles.channelName}>
                                {this.props.playerProps.mediaName}
                            </span>
                            <span className={styles.timeStamp}>
                                le&nbsp;
                                {this.state.now.toLocaleDateString(
                                    "fr-FR",
                                    dateOptions as any
                                )}
                                &nbsp;à&nbsp;
                                {hours < 10 ? "0" : ""}
                                {hours}:{minutes < 10 ? "0" : ""}
                                {minutes}:{seconds < 10 ? "0" : ""}
                                {seconds}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private onPlayPauseClick() {
        if (this.player === null || !this.state.ready) {
            return;
        }
        if (this.player.paused) {
            this.player
                .play()
                .then(() => {
                    this.setState({ playing: true });
                    if (typeof this.props.onPlayPause !== "undefined") {
                        this.props.onPlayPause(true);
                    }
                })
                .catch((err) => {
                    console.warn(err);
                    this.setState({
                        playing: false,
                        ready: false,
                    });
                    if (typeof this.props.onPlayPause !== "undefined") {
                        this.props.onPlayPause(false);
                    }
                });
        } else {
            this.player.pause();
            this.setState({ playing: false });
            if (typeof this.props.onPlayPause !== "undefined") {
                this.props.onPlayPause(false);
            }
        }
    }

    private onSpeedClick() {
        let speed: 1 | 2 | 5 | 0.5 = 1;
        switch (this.state.speed) {
            case 5:
                speed = 0.5;
                break;
            case 2:
                speed = 5;
                break;
            case 1:
                speed = 2;
                break;
            case 0.5:
            default:
                speed = 1;
                break;
        }
        this.setState({ speed: speed });
        if (this.player !== null && this.state.ready) {
            this.player.playbackRate = speed;
        }
    }

    private onFragChange(_: Event, data: any) {
        if (
            isNullOrUndefined(data) ||
            isNullOrUndefined(data.frag) ||
            isNullOrUndefinedOrZero(data.frag.programDateTime)
        ) {
            console.warn({ data }, "Invalid Fragment change data");
            return;
        }
        const fragTime = new Date(data.frag.programDateTime);
        if (isNullOrUndefined(this.props.playerProps.start)) {
            console.warn({ data }, "Frag change but no startAt ...");
            return;
        }
        if (true) {
            this.firstFragTime = fragTime;

            let currentTime =
                (this.props.start.getTime() - fragTime.getTime()) / 1000;
            let valid = false;

            // tslint:disable-next-line
            // @ts-ignore
            for (let i = 0; i < this.player.buffered.length; i++) {
                if (
                    // @ts-ignore
                    currentTime >= this.player.buffered.start(i) &&
                    // @ts-ignore
                    currentTime <= this.player.buffered.end(i)
                ) {
                    valid = true;
                    break;
                }
            }

            if (!valid && this.player !== null) {
                // @ts-ignore
                currentTime = this.player.buffered.start(
                    this.player.buffered.length - 1
                );
            }
        }
    }

    private onVolumeClick() {
        let volume: 0 | 0.25 | 0.5 | 0.75 | 1 = 0.75;
        switch (this.state.volume) {
            case 1:
                volume = 0;
                break;
            case 0:
                volume = 0.25;
                break;
            case 0.25:
                volume = 0.5;
                break;
            case 0.75:
                volume = 1;
                break;
            case 0.5:
            default:
                volume = 0.75;
                break;
        }
        this.setState({ volume: volume });
        if (this.player !== null && this.state.ready) {
            this.player.volume = volume;
        }
    }

    private onFSClick() {
        if (this.player === null) {
            return;
        }
        this.player.requestFullscreen();
    }
    private getHLSBindings(): Map<string, any> {
        const bindings = new Map<string, any>();
        bindings.set(Hls.Events.ERROR, this.onHLSError.bind(this));
        bindings.set(Hls.Events.FRAG_CHANGED, this.onFragChange.bind(this));
        return bindings;
    }
    private onHLSError(data: any) {
        switch (data.details) {
            case Hls.ErrorDetails.FRAG_LOAD_ERROR:
            case Hls.ErrorDetails.FRAG_LOAD_TIMEOUT:
                break;
            case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
            case Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT:
                break;
            default:
                break;
        }
    }
}
